/*
 * File: GetStudentCourseEnrollments.ts
 * Author: Austin Robinson
 * Date Created: 8/15/2023
 * Copyright (c) DrivingDoc 2023
 */
import { AxiosError } from 'axios';
import { createQuery } from 'react-query-kit';

import { ErrorResponse, privateApi } from 'api';

export type GetSelfScheduleEligibilityParams = {
  studentId: string;
  courseEnrollmentId: string;
};

/**
 * ISO-8601 timestamp string
 * Example: "2026-01-14T14:00:00Z"
 */
export type IsoDateTime = string;

/**
 * Top-level eligibility response for a student's course enrollment
 */
export interface StudentEligibilityResponse {
  /** Student ID */
  studentId: string;

  /** Course Enrollment ID */
  courseEnrollmentId: string;

  /**
   * All known ineligibility blocks that apply to this student,
   * including future ones (e.g. upcoming permit expiration)
   */
  blocks: EligibilityBlock[];

  /** Current account balance in cents */
  currentBalance: number;

  /**
   * Balance restrictions for different period types.
   * Frontend can use these to show warnings before booking specific periods.
   * Actual validation happens server-side during enrollment.
   */
  balanceRestrictions: {
    /** Maximum balance allowed for the first period (cents), null if no restriction */
    firstPeriod: number | null;
    /** Maximum balance allowed for the last period (cents), null if no restriction */
    lastPeriod: number | null;
    /** Maximum balance allowed for other periods (cents), null if no restriction */
    other: number | null;
  };
}

/**
 * A single rule that blocks booking during a specific time range
 */
export interface EligibilityBlock {
  /** Stable identifier for this block (e.g. "permit-expired", "interval-enrollment-123") */
  id: string;

  /**
   * How the block behaves temporally
   *
   * - blocked_until        → blocked until a date
   * - blocked_from         → blocked starting from a date
   * - blocked_between      → blocked between two dates
   * - blocked_indefinitely → blocked indefinitely until some action is taken
   */
  type: EligibilityBlockType;

  /**
   * Inclusive start of the blocked range.
   * Null means "from the beginning of time".
   */
  blocksFrom: IsoDateTime | null;

  /**
   * Inclusive end of the blocked range.
   * Null means "forever" or "until resolved".
   */
  blocksUntil: IsoDateTime | null;

  /** Why the student is blocked */
  reason: EligibilityBlockReason;

  /** What the student can do about it */
  resolution: EligibilityBlockResolution;
}

export type EligibilityBlockType = 'blocked_until' | 'blocked_from' | 'blocked_between' | 'blocked_indefinitely';

/**
 * Human-readable + machine-readable reason
 */
export interface EligibilityBlockReason {
  /** Stable code used by frontend logic */
  code:
    | 'OUTSTANDING_BALANCE'
    | 'PERMIT_EXPIRED'
    | 'PERMIT_MISSING'
    | 'PERMIT_EXPIRATION_MISSING'
    | 'PERMIT_EXPIRING'
    | 'ENROLLMENT_NOT_OPEN'
    | 'ENROLLMENT_CLOSED'
    | 'MIN_INTERVAL_BETWEEN_DRIVES'
    | 'MAX_ADVANCE_BOOKINGS'
    | string; // allow future expansion

  /** Short UI title (e.g., "Permit Required") */
  title: string;

  /** Full user-facing explanation */
  message: string;

  /** Optional structured data (balance amounts, permit dates, related bookings, etc) */
  data?: Record<string, unknown>;
}

/**
 * How this block can be resolved
 */
export interface EligibilityBlockResolution {
  action: 'WAIT' | 'PAY' | 'UPDATE_PERMIT' | 'CONTACT_ADMIN' | 'CANCEL_BOOKING' | 'NONE';

  /** Optional message explaining the action */
  message?: string;

  /** Optional deep link for the UI (e.g., /account/permit, /payments) */
  url?: string;

  /** Optional amount needed to resolve (for PAY action) */
  amountDue?: number;
}

async function getSelfScheduleEligibility({ studentId, courseEnrollmentId }: GetSelfScheduleEligibilityParams) {
  // Get the user's timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // make api call
  const result = await privateApi.get<StudentEligibilityResponse>(
    `${import.meta.env.VITE_API_BASE_URL}/users/students/${studentId}/courseEnrollments/${courseEnrollmentId}/selfScheduleEligibility`,
    {
      params: { timezone },
    },
  );

  return result.data;
}

const useGetSelfScheduleEligibility = createQuery<
  StudentEligibilityResponse,
  GetSelfScheduleEligibilityParams,
  AxiosError<ErrorResponse>
>({
  queryKey: ['self_schedule_eligibility'],
  fetcher: (variables) => getSelfScheduleEligibility(variables),
});

export { useGetSelfScheduleEligibility };
